import "@/index.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { I18nProvider } from "@/i18n/I18nContext";
import { MemberAuthProvider } from "@/member/MemberAuthContext";
import { AccessibilityProvider } from "@/member/AccessibilityContext";
import MemberProtectedRoute from "@/member/components/MemberProtectedRoute";
import MemberLogin from "@/member/pages/MemberLogin";
import Start from "@/member/pages/Start";
import Activities, { ActivityFilter, ActivityMap, ActivitySeries } from "@/member/pages/Activities";
import { ActivityQuestion, ActivityCompanion, ActivityWaitlist, ActivityChange, ActivityManage, ActivityEdit } from "@/member/pages/ActivityFlows";
import Groups from "@/member/pages/Groups";
import Messages from "@/member/pages/Messages";
import MessageThread from "@/member/pages/MessageThread";
import { MessageMedia, MessageAttach, MessageReport, MessageMute } from "@/member/pages/MessageFlows";
import Profile from "@/member/pages/Profile";
import { ProfileVisibility, ProfilePhoto } from "@/member/pages/ProfileExtras";
import NotFound from "@/member/pages/NotFound";
import ActivityDetail, { ActivityParticipation, ActivityLeave, ActivityAccess } from "@/member/pages/ActivityDetail";
import GroupDetail from "@/member/pages/GroupDetail";
import { GroupRules, GroupMembers, GroupMeetings } from "@/member/pages/GroupSections";
import { GroupPropose, GroupInvitation, GroupRequests, GroupLeave } from "@/member/pages/GroupFlows";
import People, { PeopleFilter } from "@/member/pages/People";
import PersonDetail from "@/member/pages/PersonDetail";
import Contacts, { ConfirmedContacts } from "@/member/pages/Contacts";
import { ContactCompose, ContactIncoming, ContactSent, ContactBlock } from "@/member/pages/ContactFlows";
import { OwnMemberView, SharedGroups, BlockedContacts } from "@/member/pages/PeopleExtras";
import Plans, { PlansList } from "@/member/pages/Plans";
import { PlanCreate, PlanChecklist, PlanDetail } from "@/member/pages/PlanFlows";
import { SharedPlans, SharedPlanDetail } from "@/member/pages/SharedPlans";
import { TrustedInvite, CheckIn } from "@/member/pages/SafetyFlows";
import Community, { CommunityFilter } from "@/member/pages/Community";
import Search from "@/member/pages/Search";
import Safety from "@/member/pages/Safety";
import Settings from "@/member/pages/Settings";
import { NotificationSettings, AccountSecurity, SessionsSignOut } from "@/member/pages/SettingsFlows";
import { DataExport, ExportStatus, AccountDelete } from "@/member/pages/AccountData";
import Moderation from "@/member/pages/Moderation";
import { Organizations, OrganizationProfile, OrganizationFollow } from "@/member/pages/Organizations";
import Setup from "@/member/pages/Setup";
import Region from "@/member/pages/Region";
import { FIXTURE_MODE } from "@/lib/fixtureMode";
import AccessPass, { AccessPassSection } from "@/member/pages/AccessPass";
import { Help, More } from "@/member/pages/HelpMore";
import UnavailableView from "@/member/pages/UnavailableView";
import { LinkExpired, EmailHelp } from "@/member/pages/PublicPages";
import Invitation from "@/member/pages/Invitation";
import { Register, VerifyEmail, ForgotPassword, ResetPassword, LegalPage } from "@/member/pages/Access";
import PostCompose from "@/member/pages/PostCompose";
import PostDetail from "@/member/pages/PostDetail";
import ActivityCreate from "@/member/pages/ActivityCreate";
import AccessPassShare from "@/member/pages/AccessPassShare";
import AccessPassGrants, { GrantDetail, RequestDetail } from "@/member/pages/AccessPassGrants";
import { ReceivedList, ReceivedDetail } from "@/member/pages/AccessPassReceived";
import { Saved, Notifications } from "@/member/pages/SavedNotifications";
import { MEMBER_VIEWS } from "@/member/views";

// Standalone Visibilis Life member app (app.visibilis.life).
// Shares the same secure backend via REACT_APP_BACKEND_URL.
function ResponsiveToaster() {
  const [position, setPosition] = useState("top-right");
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const apply = () => setPosition(mq.matches ? "bottom-center" : "top-right");
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  // Desktop keeps the original top-right position; on mobile the toast moves to the
  // bottom (above the fixed bottom navigation) so it never covers the header, page
  // title or nav. Toast design, colors and icons are unchanged.
  return <Toaster position={position} richColors mobileOffset={{ bottom: 88, left: 16, right: 16 }} />;
}

// Move focus to the page content after navigation so screen readers announce the new view.
function RouteFocus() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const main = document.getElementById("member-main") || document.getElementById("visibilis-login-main");
    if (main) main.focus({ preventScroll: true });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <I18nProvider>
      <MemberAuthProvider>
        <AccessibilityProvider>
          <BrowserRouter>
            <RouteFocus />
            <Routes>
              <Route path="/login" element={<MemberLogin />} />
              <Route path="/anmelden" element={<MemberLogin variant="detail" />} />
              <Route path="/registrieren" element={<Register />} />
              <Route path="/email-bestaetigen" element={<VerifyEmail />} />
              <Route path="/passwort-vergessen" element={<ForgotPassword />} />
              <Route path="/passwort-neu" element={<ResetPassword />} />
              <Route path="/rechtliches/:doc" element={<LegalPage />} />
              <Route path="/link-abgelaufen" element={<LinkExpired />} />
              <Route path="/email-hilfe" element={<EmailHelp />} />
              <Route path="/einladung" element={<Invitation />} />
              <Route element={<MemberProtectedRoute />}>
                <Route index element={<Start />} />
                <Route path="/einrichtung" element={<Setup step="profil" />} />
                <Route path="/einrichtung/interessen" element={<Setup step="interessen" />} />
                <Route path="/einrichtung/ansicht" element={<Setup step="ansicht" />} />
                <Route path="/einrichtung/privatsphaere" element={<Setup step="privatsphaere" />} />
                <Route path="/einrichtung/fertig" element={<Setup step="fertig" />} />
                <Route path="/einstellungen/darstellung" element={<Setup step="darstellung" />} />
                <Route path="/einstellungen/region" element={<Region />} />
                <Route path="/suche" element={<Search />} />
                <Route path="/community" element={<Community />} />
                <Route path="/community/filtern" element={<CommunityFilter />} />
                <Route path="/community/neu" element={<PostCompose />} />
                <Route path="/community/beitraege" element={<Navigate to="/community" replace />} />
                <Route path="/community/beitraege/:id" element={<PostDetail />} />
                <Route path="/menschen" element={<People />} />
                <Route path="/menschen/filtern" element={<PeopleFilter />} />
                <Route path="/menschen/:id" element={<PersonDetail />} />
                <Route path="/kontakte" element={<Contacts />} />
                <Route path="/kontakte/bestaetigt" element={<ConfirmedContacts />} />
                <Route path="/kontakte/neu/:id" element={<ContactCompose />} />
                <Route path="/kontakte/anfragen/:id" element={<ContactIncoming />} />
                <Route path="/kontakte/gesendet/:id" element={<ContactSent />} />
                <Route path="/kontakte/blockieren/:id" element={<ContactBlock />} />
                <Route path="/kreise/gemeinsam/:id" element={<SharedGroups />} />
                <Route path="/sicherheit/blockiert" element={<BlockedContacts />} />
                <Route path="/profil/vorschau" element={<OwnMemberView />} />
                <Route path="/aktivitaeten" element={<Activities />} />
                <Route path="/aktivitaeten/neu" element={<ActivityCreate />} />
                <Route path="/aktivitaeten/zugang" element={<ActivityFilter />} />
                <Route path="/aktivitaeten/karte" element={<ActivityMap />} />
                <Route path="/aktivitaeten/serie/:id" element={<ActivitySeries />} />
                <Route path="/aktivitaeten/verwalten" element={<ActivityManage />} />
                <Route path="/aktivitaeten/verwalten/aendern" element={<ActivityEdit />} />
                <Route path="/aktivitaeten/:id" element={<ActivityDetail />} />
                <Route path="/aktivitaeten/:id/teilnahme" element={<ActivityParticipation />} />
                <Route path="/aktivitaeten/:id/absagen" element={<ActivityLeave />} />
                <Route path="/aktivitaeten/:id/zugang" element={<ActivityAccess />} />
                <Route path="/aktivitaeten/:id/frage" element={<ActivityQuestion />} />
                <Route path="/aktivitaeten/:id/begleitung" element={<ActivityCompanion />} />
                <Route path="/aktivitaeten/:id/warteliste" element={<ActivityWaitlist />} />
                <Route path="/aktivitaeten/:id/aenderung" element={<ActivityChange />} />
                <Route path="/kreise" element={<Groups />} />
                <Route path="/kreise/vorschlagen" element={<GroupPropose />} />
                <Route path="/kreise/einladungen" element={<GroupInvitation />} />
                <Route path="/kreise/aufnahmeanfragen" element={<GroupRequests />} />
                <Route path="/kreise/:id" element={<GroupDetail />} />
                <Route path="/kreise/:id/regeln" element={<GroupRules />} />
                <Route path="/kreise/:id/mitglieder" element={<GroupMembers />} />
                <Route path="/kreise/:id/treffen" element={<GroupMeetings />} />
                <Route path="/kreise/:id/verlassen" element={<GroupLeave />} />
                <Route path="/gruppen" element={<Navigate to="/kreise" replace />} />
                <Route path="/nachrichten" element={<Messages />} />
                <Route path="/nachrichten/:id" element={<MessageThread />} />
                <Route path="/nachrichten/:id/medien" element={<MessageMedia />} />
                <Route path="/nachrichten/:id/anhang" element={<MessageAttach />} />
                <Route path="/nachrichten/:id/melden/:messageId" element={<MessageReport />} />
                <Route path="/nachrichten/:id/stummschalten" element={<MessageMute />} />
                <Route path="/plaene" element={<Plans />} />
                <Route path="/plaene/liste" element={<PlansList />} />
                <Route path="/plaene/neu" element={<PlanCreate />} />
                <Route path="/plaene/checkliste" element={<PlanChecklist />} />
                <Route path="/plaene/geteilt" element={<SharedPlans />} />
                <Route path="/plaene/:id/geteilt/:owner" element={<SharedPlanDetail />} />
                <Route path="/plaene/:id" element={<PlanDetail />} />
                <Route path="/merkliste" element={<Saved />} />
                <Route path="/benachrichtigungen" element={<Notifications />} />
                <Route path="/access-pass" element={<AccessPass />} />
                <Route path="/access-pass/teilen" element={<AccessPassShare />} />
                <Route path="/access-pass/freigaben" element={<AccessPassGrants />} />
                <Route path="/access-pass/freigaben/:id" element={<GrantDetail />} />
                <Route path="/access-pass/anfragen/:id" element={<RequestDetail />} />
                <Route path="/access-pass/empfangen" element={<ReceivedList />} />
                <Route path="/access-pass/empfangen/:id" element={<ReceivedDetail />} />
                <Route path="/access-pass/:section" element={<AccessPassSection />} />
                <Route path="/profil" element={<Profile />} />
                <Route path="/profil/sichtbarkeit" element={<ProfileVisibility />} />
                <Route path="/profil/foto" element={<ProfilePhoto />} />
                <Route path="/sicherheit" element={<Safety />} />
                <Route path="/sicherheit/vertraute-kontakte" element={<TrustedInvite />} />
                <Route path="/sicherheit/rueckmeldung" element={<CheckIn />} />
                <Route path="/einstellungen" element={<Settings />} />
                <Route path="/einstellungen/benachrichtigungen" element={<NotificationSettings />} />
                <Route path="/einstellungen/sicherheit" element={<AccountSecurity />} />
                <Route path="/einstellungen/sitzungen" element={<SessionsSignOut />} />
                <Route path="/einstellungen/daten" element={<DataExport />} />
                <Route path="/einstellungen/daten/:id" element={<ExportStatus />} />
                <Route path="/einstellungen/konto-loeschen" element={<AccountDelete />} />
                <Route path="/hilfe" element={<Help />} />
                <Route path="/hilfe/moderation" element={<Moderation />} />
                <Route path="/organisationen" element={<Organizations />} />
                <Route path="/organisationen/:id" element={<OrganizationProfile />} />
                <Route path="/organisationen/:id/folgen" element={<OrganizationFollow />} />
                <Route path="/mehr" element={<More />} />
                {MEMBER_VIEWS.map((v) => <Route key={v.path} path={v.path} element={<UnavailableView view={v} />} />)}
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          <ResponsiveToaster />
          {FIXTURE_MODE && <p role="note" data-testid="fixture-badge" className="pointer-events-none fixed bottom-[78px] right-2 z-50 rounded-full bg-v3-sand px-3 py-1 text-[11px] font-bold text-v3-amber shadow lg:bottom-2">Testmodus · fiktive Daten</p>}
        </AccessibilityProvider>
      </MemberAuthProvider>
    </I18nProvider>
  );
}

export default App;
